import type { SortDir } from "shared";

export interface SortState<K extends string> {
	key: K;
	dir: SortDir;
}

interface SortHeaderProps<K extends string> {
	label: string;
	sortKey: K;
	sort: SortState<K>;
	onSort: (next: SortState<K>) => void;
	/** Direction applied when this column becomes active (text: asc, counts/dates: desc). */
	defaultDir?: SortDir;
}

/**
 * Clickable `<th>` for server-side table sorting (order comes from the API,
 * so it holds across "Load more"). Clicking the active column toggles the
 * direction; clicking a new column starts at `defaultDir`.
 */
export function SortHeader<K extends string>({
	label,
	sortKey,
	sort,
	onSort,
	defaultDir = "asc",
}: SortHeaderProps<K>) {
	const active = sort.key === sortKey;
	const next: SortState<K> = active
		? { key: sortKey, dir: sort.dir === "asc" ? "desc" : "asc" }
		: { key: sortKey, dir: defaultDir };
	return (
		<th
			className="px-3 py-2 font-medium"
			aria-sort={
				active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"
			}
		>
			<button
				type="button"
				className="inline-flex items-center gap-1 hover:text-fg"
				onClick={() => onSort(next)}
			>
				{label}
				<span
					aria-hidden="true"
					className={`text-[10px] ${active ? "text-accent" : "text-fg-muted/50"}`}
				>
					{active ? (sort.dir === "asc" ? "▲" : "▼") : "⇅"}
				</span>
			</button>
		</th>
	);
}
